import { createFileRoute, Outlet, useLocation, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessagingKeysDialog } from "@/components/app/messaging/actions/messaging-keys-dialog";
import { useKeysHelp } from "@/components/app/messaging/actions/use-keys-help";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { inboxQuery } from "@/lib/messaging/live/queries";
import { inboxTitle, useUnsentCount } from "@/lib/messaging/compose/unsent";
import { unreadCount } from "@/lib/messaging/history/unread-count";
import { ConnectionStrip } from "../-components/conversation/connection-strip";
import { ConversationSidebar } from "../-components/conversation/conversation-sidebar";
import { inboxPageOpen } from "../-components/home/inbox-page-open";
import { useFillHeight } from "../-components/thread/use-fill-height";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/messaging/_inbox")({
  component: InboxLayout,
});

/** The inbox beside the open conversation, edge to edge under the header; on a phone, one or the
 * other. */
function InboxLayout() {
  const { conversationId } = useParams({ strict: false });
  const pathname = useLocation({ select: (location) => location.pathname });
  const frame = useFillHeight<HTMLDivElement>();
  const inbox = useQuery(inboxQuery());
  const { count } = unreadCount(inbox.data ?? []);
  useDocumentTitle(inboxTitle(count, useUnsentCount()));
  const open = Boolean(conversationId) || inboxPageOpen(pathname);
  const [keys, setKeys] = useKeysHelp(frame);
  return (
    <div
      ref={frame}
      className="bg-card -mx-4 flex min-h-0 flex-col overflow-hidden border-y md:mx-0 md:border-y-0"
    >
      <ConnectionStrip />
      <div className="flex min-h-0 flex-1">
        <aside
          aria-label="Conversations"
          className={cn(
            "bg-sidebar flex min-h-0 w-full flex-col border-e md:w-72 md:shrink-0",
            open && "max-md:hidden",
          )}
        >
          <ConversationSidebar selected={conversationId ?? null} headingLevel={open ? 2 : 1} />
        </aside>
        <section className={cn("flex min-h-0 min-w-0 flex-1 flex-col", !open && "max-md:hidden")}>
          <Outlet />
        </section>
      </div>
      <MessagingKeysDialog open={keys} onOpenChange={setKeys} />
    </div>
  );
}
